import { useState, useRef } from 'react'
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaExclamationCircle, FaArrowRight, FaWhatsapp } from 'react-icons/fa'
import { submitContact } from '../utils/api'
import VariableProximity from '../components/VariableProximity'

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  })
  const [errors, setErrors] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const contactHeadingRef = useRef(null)
  const businessHoursRef = useRef(null)

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const validatePhone = (phone) => {
    return /^[\d\s\-\(\)]+$/.test(phone) && phone.replace(/\D/g, '').length >= 10
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const newErrors = {}

    // Validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required'
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number'
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    try {
      const result = await submitContact(formData)
      if (result.success) {
        setSubmitted(true)
        setFormData({ name: '', email: '', phone: '', message: '' })
        setErrors({})
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSubmitted(false)
        }, 5000)
      } else {
        setErrors({ submit: result.error || 'Failed to submit form' })
      }
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to connect to server. Please try again later.' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      {/* Header */}
      <section className="section-padding bg-transparent text-white pt-24 border-b border-dark-700/30">
        <div className="container-custom text-center">
          <div
            ref={contactHeadingRef}
            style={{ position: 'relative' }}
            className="mb-4"
          >
            <VariableProximity
              label="Contact Us"
              className="text-5xl font-bold"
              fromFontVariationSettings="'wght' 400, 'opsz' 9"
              toFontVariationSettings="'wght' 1000, 'opsz' 40"
              containerRef={contactHeadingRef}
              radius={100}
              falloff="linear"
              style={{ color: 'white' }}
            />
          </div>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Have questions? Want to learn more about our dispatch services? 
            Get in touch with us today. We are here to help.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-transparent">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-gradient-to-br from-dark-800/80 to-dark-700/80 backdrop-blur-sm rounded-xl shadow-lg p-8 border border-dark-600/30">
              <h2 className="text-3xl font-bold text-white mb-6">Send Us a Message</h2>
              
              {submitted && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                  <FaCheckCircle className="text-green-600 mr-3" size={20} />
                  <span className="text-green-800">
                    Thank you! Your message has been sent. We will get back to you soon.
                  </span>
                </div>
              )}

              {errors.submit && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
                  <FaExclamationCircle className="text-red-600 mr-3" size={20} />
                  <span className="text-red-800">{errors.submit}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-200 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-dark-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-400 ${
                      errors.name ? 'border-red-500' : 'border-dark-500/30'
                    }`}
                    placeholder="John Doe"
                  />
                  {errors.name && (
                    <div className="mt-1 flex items-center text-red-600 text-sm">
                      <FaExclamationCircle className="mr-1" size={14} />
                      {errors.name}
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-200 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-dark-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-400 ${
                      errors.email ? 'border-red-500' : 'border-dark-500/30'
                    }`}
                    placeholder="john@example.com"
                  />
                  {errors.email && (
                    <div className="mt-1 flex items-center text-red-600 text-sm">
                      <FaExclamationCircle className="mr-1" size={14} />
                      {errors.email}
                    </div>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-200 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-dark-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-400 ${
                      errors.phone ? 'border-red-500' : 'border-dark-500/30'
                    }`}
                    placeholder="(630) 984-1460"
                  />
                  {errors.phone && (
                    <div className="mt-1 flex items-center text-red-600 text-sm">
                      <FaExclamationCircle className="mr-1" size={14} />
                      {errors.phone}
                    </div>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-200 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className={`w-full px-4 py-3 bg-dark-700/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent-400 resize-none ${
                      errors.message ? 'border-red-500' : 'border-dark-500/30'
                    }`}
                    placeholder="Tell us about your dispatch needs..."
                  />
                  {errors.message && (
                    <div className="mt-1 flex items-center text-red-600 text-sm">
                      <FaExclamationCircle className="mr-1" size={14} />
                      {errors.message}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full btn-primary ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className="flex items-center justify-center">
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <FaArrowRight className="ml-2" />}
                  </span>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-gradient-to-br from-dark-800/80 to-dark-700/80 backdrop-blur-sm rounded-xl shadow-lg p-8 mb-8 border border-dark-600/30 transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-2xl hover:shadow-accent-400/20 hover:border-accent-400/50 cursor-pointer group">
                <h2 className="text-3xl font-bold text-white mb-6 transition-colors duration-300 group-hover:text-accent-400">Get In Touch</h2>
                <p className="text-gray-300 mb-8 transition-colors duration-300 group-hover:text-gray-200">
                  We are here to answer your questions and help you get started with our dispatch services. 
                  Reach out to us through any of the following methods.
                </p>

                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Phone */}
                  <div className="flex flex-col items-center text-center flex-1 transition-all duration-300 hover:scale-105 group/item">
                    <div className="bg-accent-400/20 p-3 rounded-lg mb-3 w-fit transition-all duration-300 group-hover/item:bg-accent-400/30 group-hover/item:scale-110">
                      <FaWhatsapp className="text-accent-400 transition-transform duration-300 group-hover/item:rotate-12" size={22} />
                    </div>
                    <h3 className="font-semibold text-white mb-1 transition-colors duration-300 group-hover/item:text-accent-400">Whatsapp</h3>
                    <a href="tel:+16309841460" className="text-accent-400 hover:text-accent-300 font-medium mb-1 transition-colors duration-300">
                      +1 (630) 984-1460
                    </a>
                    <p className="text-sm text-gray-300 transition-colors duration-300 group-hover/item:text-gray-200">Mon-Fri 8am-6pm EST</p>
                  </div>

                  {/* Email */}
                  <div className="flex flex-col items-center text-center flex-1 transition-all duration-300 hover:scale-105 group/item">
                    <div className="bg-accent-400/20 p-3 rounded-lg mb-3 w-fit transition-all duration-300 group-hover/item:bg-accent-400/30 group-hover/item:scale-110">
                      <FaEnvelope className="text-accent-400 transition-transform duration-300 group-hover/item:rotate-12" size={22} />
                    </div>
                    <h3 className="font-semibold text-white mb-1 transition-colors duration-300 group-hover/item:text-accent-400">Email</h3>
                    <a href="mailto:info@ewologistics.com" className="text-accent-400 hover:text-accent-300 font-medium mb-1 transition-colors duration-300">
                      info@ewologistics.com
                    </a>
                    <p className="text-sm text-gray-300 transition-colors duration-300 group-hover/item:text-gray-200">We respond within 24 hours</p>
                  </div>

                  {/* Address */}
                  <div className="flex flex-col items-center text-center flex-1 transition-all duration-300 hover:scale-105 group/item">
                    <div className="bg-accent-400/20 p-3 rounded-lg mb-3 w-fit transition-all duration-300 group-hover/item:bg-accent-400/30 group-hover/item:scale-110">
                      <FaMapMarkerAlt className="text-accent-400 transition-transform duration-300 group-hover/item:rotate-12" size={22} />
                    </div>
                    <h3 className="font-semibold text-white mb-1 transition-colors duration-300 group-hover/item:text-accent-400">Address</h3>
                    <p className="text-gray-300 text-sm transition-colors duration-300 group-hover/item:text-gray-200">
                      453 W Fullerton Ave<br />
                      Elmhurst, IL 60126<br />
                      United States
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="bg-gradient-to-br from-dark-800/80 to-dark-700/80 backdrop-blur-sm rounded-xl shadow-lg overflow-hidden border border-dark-600/30">
                <div className="w-full h-96">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2976.5!2d-87.9401!3d41.8996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x880e4c1234567890%3A0x1234567890abcdef!2s453%20W%20Fullerton%20Ave%2C%20Elmhurst%2C%20IL%2060126!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="EW Logistics & IT Solution LLC Location"
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Hours */}
      <section className="section-padding bg-transparent">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div
              ref={businessHoursRef}
              style={{ position: 'relative' }}
              className="mb-6"
            >
              <VariableProximity
                label="Business Hours"
                className="text-3xl font-bold text-white"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={businessHoursRef}
                radius={100}
                falloff="linear"
              />
            </div>
            <div className="bg-black/25 backdrop-blur-sm rounded-xl p-8 border border-white/10 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div>
                  <p className="font-semibold text-gray-200">Monday - Friday</p>
                  <p className="text-gray-300">8:00 AM - 6:00 PM EST</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-200">Saturday</p>
                  <p className="text-gray-300">9:00 AM - 2:00 PM EST</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-200">Sunday</p>
                  <p className="text-gray-300">Closed</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-200">Emergency Dispatch</p>
                  <p className="text-accent-400 font-semibold">24/7 Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact

